<template>
  <b-form @submit.prevent="handleSubmit">
    <!-- Thông tin cơ bản -->
    <div class="form-section mb-6">
      <h6 class="section-title">Thông tin cơ bản</h6>
      <b-row>
        <b-col cols="12">
          <basic-input
            :value.sync="form.name"
            name="name"
            label="Tên gói dịch vụ"
            placeholder="VD: Gói Chăm Sóc Sức Khỏe Toàn Diện"
            validate="required"
            v-validate="'required'"
            data-vv-as="Tên gói dịch vụ"
          />
          <span class="text-danger">{{ errors.first('name') }}</span>
        </b-col>

        <b-col cols="12">
          <basic-text-area
            :value.sync="form.description"
            name="description"
            label="Mô tả"
            placeholder="Mô tả chi tiết về gói dịch vụ..."
            :rows="4"
          />
        </b-col>

        <b-col cols="4">
          <basic-input
            :value.sync="form.originalPrice"
            name="originalPrice"
            label="Giá gốc (VNĐ)"
            placeholder="0"
            type="number"
            validate="required"
            v-validate="'required|numeric|min_value:0'"
            data-vv-as="Giá gốc"
          />
          <span class="text-danger">{{ errors.first('originalPrice') }}</span>
        </b-col>

        <b-col cols="4">
          <basic-input
            :value.sync="form.currentPrice"
            name="currentPrice"
            label="Giá bán (VNĐ)"
            placeholder="0"
            type="number"
            validate="required"
            v-validate="'required|numeric|min_value:0'"
            data-vv-as="Giá bán"
          />
          <span class="text-danger">{{ errors.first('currentPrice') }}</span>
        </b-col>

        <b-col cols="4">
          <basic-select
            label="Trạng thái"
            placeholder="-- Chọn --"
            name="packageStatus"
            :options="statusOptions"
            track-by="value"
            value-label="text"
            :value.sync="statusValue"
            @input="form.status = $event"
          />
        </b-col>

        <b-col cols="5">
          <basic-input
            :value.sync="form.months"
            name="months"
            label="Thời hạn sử dụng (tháng)"
            type="number"
            validate="required"
            v-validate="'required|numeric|min_value:1'"
            data-vv-as="Thời hạn sử dụng"
          />
          <span class="text-danger">{{ errors.first('months') }}</span>
          <small class="text-success">Thời gian có hiệu lực kể từ ngày khách hàng đăng ký</small>
        </b-col>
      </b-row>
    </div>

    <!-- Hiển thị trên Home app -->
    <div class="form-section mb-6">
      <h6 class="section-title">Hiển thị trên trang Home của app</h6>
      <b-row>
        <b-col cols="12" class="mb-4">
          <label class="font-weight-bold d-block mb-2">Logo đối tác</label>
          <div class="d-flex align-items-center">
            <label class="btn btn-light-success btn-sm mr-3 mb-0">
              <i class="la la-image"></i> Chọn ảnh
              <input type="file" accept="image/png,image/jpeg,image/jpg" class="d-none" @change="onLogoSelected" />
            </label>
            <span v-if="logo.uploading" class="text-warning small"><i class="la la-spinner la-spin"></i> Đang upload...</span>
            <span v-else-if="logo.error" class="text-danger small">Lỗi upload, vui lòng chọn lại</span>
          </div>
          <div v-if="logoPreviewUrl" class="mt-2 d-flex align-items-center">
            <img :src="logoPreviewUrl" alt="logo" class="logo-preview" />
            <b-button size="sm" variant="light-danger" class="ml-2" :disabled="logo.uploading" @click="removeLogo">
              <i class="la la-trash"></i>
            </b-button>
          </div>
        </b-col>

        <b-col cols="12">
          <basic-text-area
            v-model="form.homeDescription"
            name="homeDescription"
            label="Mô tả hiển thị trên Home"
            placeholder="VD: Đồng hành cùng bạn kiểm soát đường huyết..."
            :rows="3"
            maxlength="500"
          />
          <small class="text-muted">{{ (form.homeDescription || '').length }}/500 ký tự</small>
        </b-col>
      </b-row>
    </div>

    <!-- Thuốc & TPCN -->
    <div class="form-section mb-6">
      <div class="section-header" @click="showMedicine = !showMedicine">
        <h6 class="section-title mb-0">
          Thuốc &amp; Thực phẩm chức năng
          <span class="count-badge text-success">({{ selectedMedicineIds.length }})</span>
        </h6>
        <i :class="showMedicine ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="toggle-icon"></i>
      </div>
      <b-collapse v-model="showMedicine">
        <div class="mt-4">
          <div v-if="loadingMedicines" class="text-center py-4">
            <b-spinner small></b-spinner> Đang tải...
          </div>
          <div v-else>
            <div class="search-box mb-3">
              <i class="fas fa-search search-box__icon"></i>
              <input v-model="medicineSearch" type="text" class="form-control search-box__input" placeholder="Tìm kiếm thuốc..." />
            </div>
            <div class="item-list-scroll">
              <div
                v-for="med in filteredMedicines"
                :key="med.id"
                class="item-checkbox-row"
                :class="{ 'item-selected': selectedMedicineIds.includes(med.id) }"
                @click="toggleMedicine(med.id)"
              >
                <b-form-checkbox :checked="selectedMedicineIds.includes(med.id)" @change="toggleMedicine(med.id)" @click.native.stop />
                <div class="item-info text-success flex-grow-1">
                  <span class="item-name">{{ med.name }}</span>
                  <span class="item-sub">• {{ formatPrice(med.price) }}/hộp</span>
                </div>
                <div v-if="selectedMedicineIds.includes(med.id)" class="qty-unlimited-row" @click.stop>
                  <template v-if="!medicineUnlimited[med.id]">
                    <label class="quantity-label">Số lượng:</label>
                    <b-form-input v-model.number="medicineQuantities[med.id]" type="number" min="1" class="qty-field" size="sm" @input="onMedicineQuantityInput(med.id)" />
                  </template>
                  <b-button :variant="medicineUnlimited[med.id] ? 'success' : 'outline-secondary'" size="sm" class="unlimited-btn" @click.stop="toggleMedicineUnlimited(med.id)">
                    {{ medicineUnlimited[med.id] ? '∞ Không giới hạn' : 'Không giới hạn' }}
                  </b-button>
                </div>
              </div>
              <div v-if="filteredMedicines.length === 0" class="text-muted text-center py-3">Không tìm thấy kết quả</div>
            </div>
          </div>
        </div>
      </b-collapse>
    </div>

    <!-- Service Package -->
    <div class="form-section mb-6">
      <div class="section-header" @click="showServicePackage = !showServicePackage">
        <h6 class="section-title text-success mb-0">
          Service Package
          <span class="count-badge">({{ selectedServicePackageIds.length }})</span>
        </h6>
        <i :class="showServicePackage ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="toggle-icon"></i>
      </div>
      <b-collapse v-model="showServicePackage">
        <div class="mt-4">
          <div v-if="loadingServicePackages" class="text-center py-4">
            <b-spinner small></b-spinner> Đang tải...
          </div>
          <div v-else>
            <div class="search-box mb-3">
              <i class="fas fa-search search-box__icon"></i>
              <input v-model="servicePackageSearch" type="text" class="form-control search-box__input" placeholder="Tìm kiếm gói dịch vụ..." />
            </div>
            <div class="item-list-scroll">
              <div
                v-for="pkg in filteredServicePackages"
                :key="pkg.id"
                class="item-checkbox-row"
                :class="{ 'item-selected': selectedServicePackageIds.includes(pkg.id) }"
                @click="toggleServicePackage(pkg.id)"
              >
                <b-form-checkbox :checked="selectedServicePackageIds.includes(pkg.id)" @change="toggleServicePackage(pkg.id)" @click.native.stop />
                <div class="item-info flex-grow-1">
                  <span class="item-name">{{ pkg.name }}</span>
                  <span class="item-sub text-success">
                    {{ pkg.totalWeek ? pkg.totalWeek + ' tuần' : '' }}
                    {{ pkg.money ? '• ' + formatPrice(pkg.money) + ' VNĐ' : '' }}
                  </span>
                </div>
                <div v-if="selectedServicePackageIds.includes(pkg.id)" class="qty-unlimited-row" @click.stop>
                  <b-button :variant="servicePackageUnlimited[pkg.id] ? 'success' : 'outline-secondary'" size="sm" class="unlimited-btn" @click.stop="toggleServicePackageUnlimited(pkg.id)">
                    {{ servicePackageUnlimited[pkg.id] ? '∞ Không giới hạn' : 'Không giới hạn' }}
                  </b-button>
                </div>
              </div>
              <div v-if="filteredServicePackages.length === 0" class="text-muted text-center py-3">Không tìm thấy kết quả</div>
            </div>
          </div>
        </div>
      </b-collapse>
    </div>

    <!-- Dịch vụ khám & điều trị -->
    <div class="form-section mb-6">
      <div class="section-header" @click="showService = !showService">
        <h6 class="section-title text-success mb-0">
          Dịch vụ khám &amp; điều trị
          <span class="count-badge">({{ selectedServiceIds.length }})</span>
        </h6>
        <i :class="showService ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="toggle-icon"></i>
      </div>
      <b-collapse v-model="showService">
        <div class="mt-4">
          <div v-if="loadingServices" class="text-center py-4">
            <b-spinner small></b-spinner> Đang tải...
          </div>
          <div v-else>
            <div class="search-box mb-3">
              <i class="fas fa-search search-box__icon"></i>
              <input v-model="serviceSearch" type="text" class="form-control search-box__input" placeholder="Tìm kiếm dịch vụ..." />
            </div>
            <div class="item-list-scroll">
              <div
                v-for="svc in filteredServices"
                :key="svc.id"
                class="item-checkbox-row"
                :class="{ 'item-selected': selectedServiceIds.includes(svc.id) }"
                @click="toggleService(svc.id)"
              >
                <b-form-checkbox :checked="selectedServiceIds.includes(svc.id)" @change="toggleService(svc.id)" @click.native.stop />
                <div class="item-info flex-grow-1">
                  <span class="item-name">{{ svc.name }}</span>
                  <span class="item-sub text-success">
                    {{ svc.minutes ? '• ' + svc.minutes + ' phút' : '' }}
                    {{ svc.price ? '• ' + formatPrice(svc.price) + '/lần' : '' }}
                  </span>
                </div>
                <div v-if="selectedServiceIds.includes(svc.id)" class="qty-unlimited-row" @click.stop>
                  <template v-if="!serviceUnlimited[svc.id]">
                    <label class="quantity-label">Số lần:</label>
                    <b-form-input v-model.number="serviceQuantities[svc.id]" type="number" min="1" class="qty-field" size="sm" @input="onQuantityInput(svc.id)" />
                  </template>
                  <b-button :variant="serviceUnlimited[svc.id] ? 'success' : 'outline-secondary'" size="sm" class="unlimited-btn" @click.stop="toggleServiceUnlimited(svc.id)">
                    {{ serviceUnlimited[svc.id] ? '∞ Không giới hạn' : 'Không giới hạn' }}
                  </b-button>
                </div>
              </div>
              <div v-if="filteredServices.length === 0" class="text-muted text-center py-3">Không tìm thấy kết quả</div>
            </div>
          </div>
        </div>
      </b-collapse>
    </div>

    <!-- Danh sách đã chọn & giảm giá -->
    <div v-if="allSelectedItems.length > 0" class="form-section mb-4">
      <h6 class="section-title">
        Danh sách đã chọn &amp; giảm giá
        <span class="count-badge text-primary">({{ allSelectedItems.length }})</span>
      </h6>
      <div v-for="item in allSelectedItems" :key="item.key" class="selected-item-row">
        <div class="selected-item-info">
          <span class="badge-type" :class="typeBadgeClass(item.type)">{{ typeBadgeLabel(item.type) }}</span>
          <span class="selected-item-name">{{ item.name }}</span>
          <span v-if="!isUnlimitedForItem(item)" class="selected-item-qty text-muted">x{{ getQtyForItem(item) }}</span>
          <span v-else class="selected-item-qty text-success">∞</span>
        </div>
        <div class="discount-row" @click.stop>
          <b-form-input
            :value="getDiscountValue(item)"
            type="number"
            min="0"
            class="discount-field"
            size="sm"
            placeholder="Giảm"
            @input="setDiscountValue(item, $event)"
          />
          <b-button-group size="sm">
            <b-button :variant="getDiscountType(item) === 0 ? 'primary' : 'outline-primary'" @click="setDiscountType(item, 0)">VNĐ</b-button>
            <b-button :variant="getDiscountType(item) === 1 ? 'primary' : 'outline-primary'" @click="setDiscountType(item, 1)">%</b-button>
          </b-button-group>
          <span class="discount-result"><strong>{{ formatPrice(calcItemPrice(item)) }} VNĐ</strong></span>
        </div>
      </div>
    </div>
  </b-form>
</template>

<script>
export default {
  name: 'PackageManagementForm',
  props: {
    value: {
      type: Object,
      default: () => ({
        id: null,
        name: '',
        description: '',
        logoImageId: null,
        homeDescription: '',
        originalPrice: 0,
        currentPrice: 0,
        status: 0,
        months: 6,
        medicineItems: [],
        servicePackageItems: [],
        serviceItems: [],
      }),
    },
  },
  data() {
    return {
      form: { ...this.value },
      isEditMode: !!(this.value && this.value.id),
      medicines: [],
      servicePackages: [],
      services: [],
      medicineSearch: '',
      servicePackageSearch: '',
      serviceSearch: '',
      loadingMedicines: false,
      loadingServicePackages: false,
      loadingServices: false,
      selectedMedicineIds: [],
      medicineQuantities: {},
      medicineUnlimited: {},
      medicineDiscounts: {},
      selectedServicePackageIds: [],
      servicePackageUnlimited: {},
      servicePackageDiscounts: {},
      selectedServiceIds: [],
      serviceQuantities: {},
      serviceUnlimited: {},
      serviceDiscounts: {},
      showMedicine: true,
      showServicePackage: true,
      showService: true,
      logo: { localUrl: null, uploading: false, error: false },
      statusOptions: [
        { value: 0, text: 'Nháp' },
        { value: 1, text: 'Hoạt động' },
        { value: 2, text: 'Ngưng hoạt động' },
      ],
    };
  },

  computed: {
    logoPreviewUrl() {
      if (this.logo.localUrl) return this.logo.localUrl;
      return this.form.logoImageId ? `${process.env.VUE_APP_API}/Image/${this.form.logoImageId}` : null;
    },
    statusValue: {
      get() { return this.statusOptions.find((o) => o.value === this.form.status) || null; },
      set(obj) { this.form.status = obj?.value ?? 0; },
    },
    filteredMedicines() {
      const q = this.medicineSearch.trim().toLowerCase();
      return q ? this.medicines.filter((m) => m.name && m.name.toLowerCase().includes(q)) : this.medicines;
    },
    filteredServicePackages() {
      const q = this.servicePackageSearch.trim().toLowerCase();
      return q ? this.servicePackages.filter((p) => p.name && p.name.toLowerCase().includes(q)) : this.servicePackages;
    },
    filteredServices() {
      const q = this.serviceSearch.trim().toLowerCase();
      return q ? this.services.filter((s) => s.name && s.name.toLowerCase().includes(q)) : this.services;
    },

    allSelectedItems() {
      const result = [];
      this.selectedMedicineIds.forEach((id) => {
        const m = this.medicines.find((x) => x.id === id);
        if (m) result.push({ key: 'med_' + id, id, type: 0, name: m.name, basePrice: m.price || 0 });
      });
      this.selectedServicePackageIds.forEach((id) => {
        const p = this.servicePackages.find((x) => x.id === id);
        if (p) result.push({ key: 'pkg_' + id, id, type: 1, name: p.name, basePrice: p.money || 0 });
      });
      this.selectedServiceIds.forEach((id) => {
        const s = this.services.find((x) => x.id === id);
        if (s) result.push({ key: 'svc_' + id, id, type: 2, name: s.name, basePrice: s.price || 0 });
      });
      return result;
    },

    totalServicePrice() {
      return this.allSelectedItems.reduce((sum, item) => sum + this.calcItemPrice(item), 0);
    },
  },

  watch: {
    value: {
      handler(newVal) {
        if (this._emitting) return; // bounce-back from our own emitUpdate, ignore
        if (JSON.stringify(newVal) === JSON.stringify(this.form)) return;
        this.form = { ...newVal };
        this.isEditMode = !!(newVal && newVal.id);
        this.initSelections();
      },
      deep: true,
      immediate: true,
    },
    form: {
      handler() { if (!this._syncingPrice && !this._initing) this.emitUpdate(); },
      deep: true,
    },
    totalServicePrice(newVal) {
      if (this._initing || (this.isEditMode && !this._userInteracted)) return;
      this._syncingPrice = true;
      this.form.originalPrice = newVal;
      this.$nextTick(() => {
        this._syncingPrice = false;
        this.emitUpdate(); // propagate updated originalPrice to parent
      });
    },
    selectedMedicineIds() { if (!this._initing) this.emitUpdate(); },
    medicineQuantities: { handler() { if (!this._initing) this.emitUpdate(); }, deep: true },
    medicineUnlimited: { handler() { if (!this._initing) this.emitUpdate(); }, deep: true },
    medicineDiscounts: { handler() { if (!this._initing) this.emitUpdate(); }, deep: true },
    selectedServicePackageIds() { if (!this._initing) this.emitUpdate(); },
    servicePackageUnlimited: { handler() { if (!this._initing) this.emitUpdate(); }, deep: true },
    servicePackageDiscounts: { handler() { if (!this._initing) this.emitUpdate(); }, deep: true },
    selectedServiceIds() { if (!this._initing) this.emitUpdate(); },
    serviceQuantities: { handler() { if (!this._initing) this.emitUpdate(); }, deep: true },
    serviceUnlimited: { handler() { if (!this._initing) this.emitUpdate(); }, deep: true },
    serviceDiscounts: { handler() { if (!this._initing) this.emitUpdate(); }, deep: true },
  },

  created() {
    this._syncingPrice = false;
    this._initing = false;
    this._userInteracted = false;
    this._emitting = false;
    this.loadMedicines();
    this.loadServicePackages();
    this.loadServices();
  },

  methods: {
    initSelections() {
      this._initing = true;

      const medItems = this.form.medicineItems || [];
      this.selectedMedicineIds = medItems.map((m) => m.medicineId);
      const medQty = {}, medUl = {}, medDisc = {};
      medItems.forEach((m) => {
        medQty[m.medicineId] = m.quantity || 1;
        medUl[m.medicineId] = !!m.isUnlimited;
        medDisc[m.medicineId] = { value: m.discountValue || 0, type: m.discountType || 0 };
      });
      this.medicineQuantities = medQty;
      this.medicineUnlimited = medUl;
      this.medicineDiscounts = medDisc;

      const pkgItems = this.form.servicePackageItems || [];
      this.selectedServicePackageIds = pkgItems.map((p) => p.servicePackageId);
      const pkgUl = {}, pkgDisc = {};
      pkgItems.forEach((p) => {
        pkgUl[p.servicePackageId] = !!p.isUnlimited;
        pkgDisc[p.servicePackageId] = { value: p.discountValue || 0, type: p.discountType || 0 };
      });
      this.servicePackageUnlimited = pkgUl;
      this.servicePackageDiscounts = pkgDisc;

      const svcItems = this.form.serviceItems || [];
      this.selectedServiceIds = svcItems.map((s) => s.serviceId);
      const svcQty = {}, svcUl = {}, svcDisc = {};
      svcItems.forEach((s) => {
        svcQty[s.serviceId] = s.quantity || 1;
        svcUl[s.serviceId] = !!s.isUnlimited;
        svcDisc[s.serviceId] = { value: s.discountValue || 0, type: s.discountType || 0 };
      });
      this.serviceQuantities = svcQty;
      this.serviceUnlimited = svcUl;
      this.serviceDiscounts = svcDisc;

      this._userInteracted = false;
      this.$nextTick(() => { this._initing = false; });
    },

    emitUpdate() {
      const medicineItems = this.selectedMedicineIds.map((id) => ({
        medicineId: id,
        quantity: this.medicineQuantities[id] || 1,
        isUnlimited: this.medicineUnlimited[id] ? 1 : 0,
        discountValue: (this.medicineDiscounts[id] || {}).value || 0,
        discountType: (this.medicineDiscounts[id] || {}).type || 0,
      }));
      const servicePackageItems = this.selectedServicePackageIds.map((id) => ({
        servicePackageId: id,
        isUnlimited: this.servicePackageUnlimited[id] ? 1 : 0,
        discountValue: (this.servicePackageDiscounts[id] || {}).value || 0,
        discountType: (this.servicePackageDiscounts[id] || {}).type || 0,
      }));
      const serviceItems = this.selectedServiceIds.map((id) => ({
        serviceId: id,
        quantity: this.serviceQuantities[id] || 1,
        isUnlimited: this.serviceUnlimited[id] ? 1 : 0,
        discountValue: (this.serviceDiscounts[id] || {}).value || 0,
        discountType: (this.serviceDiscounts[id] || {}).type || 0,
      }));
      this._emitting = true;
      this.$emit('input', { ...this.form, medicineItems, servicePackageItems, serviceItems });
      this.$nextTick(() => { this._emitting = false; });
    },

    async loadMedicines() {
      this.loadingMedicines = true;
      try {
        const { data } = await this.$api.get('BundleMedicine/All', { params: { status: 0 } });
        this.medicines = data || [];
      } catch (e) {
        console.error('Không thể tải danh sách thuốc:', e);
      } finally {
        this.loadingMedicines = false;
      }
    },
    async loadServicePackages() {
      this.loadingServicePackages = true;
      try {
        const { data } = await this.$api.get('ServicePackage', { params: { size: 1000 } });
        this.servicePackages = data?.items || data || [];
      } catch (e) {
        console.error('Không thể tải danh sách gói dịch vụ:', e);
      } finally {
        this.loadingServicePackages = false;
      }
    },
    async loadServices() {
      this.loadingServices = true;
      try {
        const { data } = await this.$api.get('BundleService/All', { params: { status: 0 } });
        this.services = data || [];
      } catch (e) {
        console.error('Không thể tải danh sách dịch vụ:', e);
      } finally {
        this.loadingServices = false;
      }
    },

    async onLogoSelected(e) {
      const file = e.target.files[0];
      e.target.value = '';
      if (!file) return;
      this.logo = { localUrl: URL.createObjectURL(file), uploading: true, error: false };
      try {
        const fd = new FormData();
        fd.append('images', file);
        fd.append('Type', 27);
        const { data } = await this.$api.post('/Image/Upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.form.logoImageId = data.id;
        this.logo.uploading = false;
      } catch (err) {
        this.logo = { localUrl: null, uploading: false, error: true };
      }
    },
    removeLogo() {
      this.logo = { localUrl: null, uploading: false, error: false };
      this.form.logoImageId = null;
    },

    toggleMedicine(id) {
      this._userInteracted = true;
      const idx = this.selectedMedicineIds.indexOf(id);
      if (idx === -1) {
        this.selectedMedicineIds = [...this.selectedMedicineIds, id];
        if (!this.medicineQuantities[id]) this.$set(this.medicineQuantities, id, 1);
        if (this.medicineUnlimited[id] === undefined) this.$set(this.medicineUnlimited, id, false);
        if (!this.medicineDiscounts[id]) this.$set(this.medicineDiscounts, id, { value: 0, type: 0 });
      } else {
        this.selectedMedicineIds = this.selectedMedicineIds.filter((x) => x !== id);
      }
    },
    toggleMedicineUnlimited(id) {
      this.$set(this.medicineUnlimited, id, !this.medicineUnlimited[id]);
      if (!this._initing) this.emitUpdate();
    },
    onMedicineQuantityInput(id) {
      if (!this.medicineQuantities[id] || this.medicineQuantities[id] < 1) this.$set(this.medicineQuantities, id, 1);
    },

    toggleServicePackage(id) {
      this._userInteracted = true;
      const idx = this.selectedServicePackageIds.indexOf(id);
      if (idx === -1) {
        this.selectedServicePackageIds = [...this.selectedServicePackageIds, id];
        if (this.servicePackageUnlimited[id] === undefined) this.$set(this.servicePackageUnlimited, id, false);
        if (!this.servicePackageDiscounts[id]) this.$set(this.servicePackageDiscounts, id, { value: 0, type: 0 });
      } else {
        this.selectedServicePackageIds = this.selectedServicePackageIds.filter((x) => x !== id);
      }
    },
    toggleServicePackageUnlimited(id) {
      this.$set(this.servicePackageUnlimited, id, !this.servicePackageUnlimited[id]);
      if (!this._initing) this.emitUpdate();
    },

    toggleService(id) {
      this._userInteracted = true;
      const idx = this.selectedServiceIds.indexOf(id);
      if (idx === -1) {
        this.selectedServiceIds = [...this.selectedServiceIds, id];
        if (!this.serviceQuantities[id]) this.$set(this.serviceQuantities, id, 1);
        if (this.serviceUnlimited[id] === undefined) this.$set(this.serviceUnlimited, id, false);
        if (!this.serviceDiscounts[id]) this.$set(this.serviceDiscounts, id, { value: 0, type: 0 });
      } else {
        this.selectedServiceIds = this.selectedServiceIds.filter((x) => x !== id);
      }
    },
    toggleServiceUnlimited(id) {
      this.$set(this.serviceUnlimited, id, !this.serviceUnlimited[id]);
      if (!this._initing) this.emitUpdate();
    },
    onQuantityInput(id) {
      if (!this.serviceQuantities[id] || this.serviceQuantities[id] < 1) this.$set(this.serviceQuantities, id, 1);
    },

    // Discount helpers
    _discountMap(item) {
      if (item.type === 0) return this.medicineDiscounts;
      if (item.type === 1) return this.servicePackageDiscounts;
      return this.serviceDiscounts;
    },
    getDiscountValue(item) { return (this._discountMap(item)[item.id] || {}).value || 0; },
    getDiscountType(item) { return (this._discountMap(item)[item.id] || {}).type || 0; },
    setDiscountValue(item, val) {
      const map = this._discountMap(item);
      const cur = map[item.id] || { value: 0, type: 0 };
      this.$set(map, item.id, { ...cur, value: Number(val) || 0 });
      this._userInteracted = true;
      if (!this._initing) this.emitUpdate();
    },
    setDiscountType(item, type) {
      const map = this._discountMap(item);
      const cur = map[item.id] || { value: 0, type: 0 };
      this.$set(map, item.id, { ...cur, type });
      this._userInteracted = true;
      if (!this._initing) this.emitUpdate();
    },
    isUnlimitedForItem(item) {
      if (item.type === 0) return !!this.medicineUnlimited[item.id];
      if (item.type === 1) return !!this.servicePackageUnlimited[item.id];
      return !!this.serviceUnlimited[item.id];
    },
    getQtyForItem(item) {
      if (item.type === 0) return this.medicineQuantities[item.id] || 1;
      if (item.type === 1) return 1;
      return this.serviceQuantities[item.id] || 1;
    },
    calcItemPrice(item) {
      const base = item.basePrice * this.getQtyForItem(item);
      const dv = this.getDiscountValue(item);
      const dt = this.getDiscountType(item);
      if (!dv) return base;
      if (dt === 1) return Math.max(0, Math.round(base * (1 - Math.min(dv, 100) / 100)));
      return Math.max(0, base - dv);
    },
    typeBadgeLabel(type) {
      if (type === 0) return 'Thuốc';
      if (type === 1) return 'Gói DV';
      return 'Dịch vụ';
    },
    typeBadgeClass(type) {
      if (type === 0) return 'badge-medicine';
      if (type === 1) return 'badge-package';
      return 'badge-service';
    },

    formatPrice(price) {
      if (!price && price !== 0) return '';
      return new Intl.NumberFormat('vi-VN').format(price);
    },

    async validate() {
      return await this.$validator.validateAll();
    },

    reset() {
      this.isEditMode = false;
      this._userInteracted = false;
      this.logo = { localUrl: null, uploading: false, error: false };
      this.form = { id: null, name: '', description: '', logoImageId: null, homeDescription: '', originalPrice: 0, currentPrice: 0, status: 0, months: 0, medicineItems: [], servicePackageItems: [], serviceItems: [] };
      this.selectedMedicineIds = [];
      this.selectedServicePackageIds = [];
      this.selectedServiceIds = [];
      this.serviceQuantities = {};
      this.medicineQuantities = {};
      this.medicineUnlimited = {};
      this.medicineDiscounts = {};
      this.servicePackageUnlimited = {};
      this.servicePackageDiscounts = {};
      this.serviceUnlimited = {};
      this.serviceDiscounts = {};
      this.$validator.reset();
    },
  },
};
</script>

<style scoped lang="scss">
.form-section {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  padding: 24px;
}
.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #181c32;
  margin-bottom: 16px;
  .count-badge { font-weight: 600; }
}
.search-box {
  position: relative;
  &__icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #a1a5b7; font-size: 0.85rem; z-index: 1; pointer-events: none; }
  &__input {
    padding-left: 34px; border-radius: 8px; border: 1px solid #e4e6ef; height: 38px; font-size: 0.9rem; background-color: #f9f9f9; transition: border-color 0.15s, background-color 0.15s;
    &:focus { border-color: #009ef7; background-color: #fff; box-shadow: 0 0 0 3px rgba(0,158,247,0.1); outline: none; }
  }
}
.item-list-scroll {
  max-height: 325px; overflow-y: auto; padding-right: 4px;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; &:hover { background: #a8a8a8; } }
}
.item-checkbox-row {
  display: flex; align-items: center; padding: 12px 14px; border: 1px solid #e4e6ef; border-radius: 8px; margin-bottom: 8px; cursor: pointer; transition: background 0.15s, border-color 0.15s; gap: 12px;
  &:hover { background-color: #f5f8fa; }
  &.item-selected { border-color: #009ef7; background-color: #f0f9ff; }
}
.item-info { display: flex; flex-direction: column; gap: 2px; }
.item-name { font-size: 0.95rem; font-weight: 600; color: #181c32; }
.item-sub { font-size: 0.82rem; }
.qty-unlimited-row {
  display: flex; align-items: center; gap: 8px; margin-left: auto;
  .quantity-label { font-size: 0.88rem; color: #5e6278; margin-bottom: 0; white-space: nowrap; }
  .qty-field { width: 70px; text-align: center; }
  .unlimited-btn { white-space: nowrap; }
}
.selected-item-row {
  display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border: 1px solid #e4e6ef; border-radius: 8px; margin-bottom: 8px; gap: 12px; flex-wrap: wrap;
}
.selected-item-info { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
.selected-item-name { font-size: 0.9rem; font-weight: 600; color: #181c32; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.selected-item-qty { font-size: 0.82rem; white-space: nowrap; }
.discount-row {
  display: flex; align-items: center; gap: 8px; flex-shrink: 0;
  .discount-field { width: 90px; }
  .discount-result { font-size: 0.82rem; white-space: nowrap; }
}
.badge-type { font-size: 0.72rem; padding: 2px 7px; border-radius: 4px; white-space: nowrap; font-weight: 600; }
.badge-medicine { background: #e8f5e9; color: #2e7d32; }
.badge-package  { background: #e3f2fd; color: #1565c0; }
.badge-service  { background: #fff3e0; color: #e65100; }
.section-header {
  display: flex; justify-content: space-between; align-items: center; cursor: pointer; user-select: none;
  &:hover .toggle-icon { color: #009ef7; }
}
.logo-preview { height: 80px; max-width: 200px; object-fit: contain; border-radius: 4px; border: 1px solid #eee; background: #fff; }
.toggle-icon { color: #a1a5b7; font-size: 0.9rem; transition: color 0.15s; }
</style>
